import { Component, AfterViewInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";

import { appStateProvider } from "../../insa-providers";
import { GenericChartsModule } from "../../insa-custom-scripts";
import { appConfig } from "../../../insaweb.config";
import { InsaService, LocalizeService } from "../../insa-services";
import { ChartModel, KendoColumnListDefinition } from "../../insa-models";

@Component({
  selector: "insaweb-donutCharts",
  templateUrl: "donut-chart.component.html",
})
export class DonutChartsComponent implements OnDestroy, AfterViewInit {
  private assetCategorySubscription;
  private currAnalysisSubscription;
  private selectedCurrency: string;
  private resizeObserver: ResizeObserver | null = null;
  private resizeRaf: number | null = null;
  private lastAppliedW: number = 0;
  private lastAppliedH: number = 0;

  constructor(
    private _insaService: InsaService,
    private _appStateProvider: appStateProvider,
    private _router: Router,
    private _localize: LocalizeService,
  ) {
    // `getLocalizedResource` returns undefined if translations aren't loaded yet,
    // so guard with `|| ''` before `.toUpperCase()` to avoid TypeError on early load.
    this.assetCategorySubscription =
      this._insaService.AssetCategoryChartEvent.subscribe((assetCategory) =>
        this.refreshDonutChart(
          assetCategory,
          "#AssetCategory",
          "#AssetCategoryList",
          (this._localize.getLocalizedResource("Res3301") || "").toUpperCase(),
        ),
      );
    this.currAnalysisSubscription =
      this._insaService.CurrencyAnalysisChartEvent.subscribe((currAnalysis) =>
        this.refreshDonutChart(
          currAnalysis,
          "#CurrencyAnalysis",
          "#CurrAnalysisList",
          (this._localize.getLocalizedResource("Res497") || "").toUpperCase(),
        ),
      );
  }

  private getDonutDims(): { w: number; h: number } {
    // Robust dynamic measurement — no per-breakpoint hardcoded values.
    // The canvas is CSS-bound to 100% of its card, which is sized by the flex row
    // (equal height with sibling cards). So `$("#insaWebDonut").height()` returns
    // the ACTUAL available pixels for this card right now, regardless of screen size.
    //
    // `.message` doesn't exist on home; the original `- $(".message").height()`
    // returned NaN so Kendo silently fell back to its default (≈400px).
    const canvasH = $("#insaWebDonut").height();
    const carouselH = $("#genericDonutCarousel").height();
    const cardH = $("#chartsCard").height();
    const carouselW =
      $("#genericDonutCarousel").width() ||
      $("#insaWebDonut").width() ||
      $("#chartsCard").width() ||
      200;
    const messageH = $(".message").length ? $(".message").height() || 0 : 0;
    // Pick the largest measurable height in the chain (most reliable). Last-ditch
    // fallback of 200 only applies if NOTHING is laid out yet (component not in DOM).
    const measuredH = (canvasH || carouselH || cardH || 200) - messageH;
    // Keep chart strictly INSIDE the card. Kendo adds a few px of padding for labels
    // and title outside `chartArea` on some builds, so subtract a safety margin. This
    // is the hard guarantee that the SVG never bleeds below the container.
    const SAFETY = 12;
    const finalH = Math.max(80, measuredH - SAFETY);
    const finalW = Math.max(80, carouselW);
    return { w: finalW, h: finalH };
  }

  // Returns dims to use for chart creation. Once onResize has run successfully and
  // captured the real container dims into lastApplied, prefer those — that way late
  // subscription events (data updates) recreate the chart at the SAME size onResize
  // already locked in, instead of re-measuring the DOM (which can return wrong values
  // mid-layout-reflow and bounce the chart back to oversized).
  private getCreationDims(): { w: number; h: number } {
    if (this.lastAppliedW > 50 && this.lastAppliedH > 50) {
      return { w: this.lastAppliedW, h: this.lastAppliedH };
    }
    return this.getDonutDims();
  }

  private refreshDonutChart(
    charData: ChartModel[],
    chartSelector: string,
    listSelector: string,
    chartName: string,
  ) {
    if (charData && Array.isArray(charData)) {
      charData.forEach((item) => {
        if (item.Description === "Equities" || item.Description === "Dionice") {
          item.Description =
            this._localize.getLocalizedResource("Res3258") || "Equities";
        }
        if (item.Description === "Bonds" || item.Description === "Obveznice") {
          item.Description =
            this._localize.getLocalizedResource("Res992") || "Bonds";
        }
      });
    }
    const { w, h } = this.getCreationDims();
    if (charData !== null && charData !== undefined && charData.length > 0) {
      let carouselOpt: CarouselOptions = {};
      carouselOpt.interval = appConfig.setCarouselTimeout;
      this.selectedCurrency = this._appStateProvider.selectedCurrency.text;
      let description = charData.sort(
        (a, b) => Number(b.Value) - Number(a.Value),
      );
      GenericChartsModule.KendoInitGenericDonutChart(
        chartSelector,
        description,
        "donut",
        "Value",
        "Description",
        chartName,
        this.legendVisible(),
        w,
        h,
        this.selectedCurrency,
      );
      GenericChartsModule.KendoGenericChartList(
        listSelector,
        description,
        this.createDonutChartList(),
      );
      $("#genericDonutCarousel").carousel(carouselOpt);
    } else {
      this.selectedCurrency = "";
      GenericChartsModule.KendoInitGenericDonutChart(
        chartSelector,
        [],
        "donut",
        "Value",
        "Description",
        chartName,
        true,
        w,
        h,
        this.selectedCurrency,
      );
      GenericChartsModule.KendoGenericChartList(listSelector, [], []);
    }
  }

  private chartDisplayedProperties() {
    if (this._router.url === "/home") {
      return true;
    } else if (this._router.url === "/chart") {
      return false;
    }
  }

  private legendVisible() {
    if (this._router.url === "/home") {
      return false;
    } else if (this._router.url === "/chart") {
      return true;
    }
  }

  private onResize(event) {
    const { w, h } = this.getDonutDims();
    // Guard: don't act on zero/tiny container dimensions (eg. card not yet visible). Keeps
    // the previous chart instance alive until the layout is genuinely ready.
    if (!w || w < 50 || !h || h < 50) return;
    // Idempotency guard: skip recreation if dimensions are essentially unchanged. Without
    // this we'd loop forever (recreate -> SVG dims change -> ResizeObserver fires -> recreate).
    if (
      Math.abs(w - this.lastAppliedW) < 2 &&
      Math.abs(h - this.lastAppliedH) < 2
    )
      return;
    this.lastAppliedW = w;
    this.lastAppliedH = h;

    // setOptions doesn't reliably re-size the underlying SVG element across all browsers/Kendo
    // builds — the chartArea numbers update but the SVG keeps its initial width/height attrs
    // from kendoChart() construction, so the donut stays at the wrong size on initial render.
    // Destroying and re-initialising the chart guarantees the SVG is regenerated with the
    // current container dimensions. Carousel state is preserved by NOT re-initialising the
    // outer Bootstrap carousel (only the inner Kendo charts are destroyed/recreated).
    try {
      const assetData = this._insaService.chartDataAssetCategory;
      const currData = this._insaService.chartDataCurrAnalysis;
      const assetTitle = (
        this._localize.getLocalizedResource("Res3301") || ""
      ).toUpperCase();
      const currTitle = (
        this._localize.getLocalizedResource("Res497") || ""
      ).toUpperCase();

      const assetChart: any = $("#AssetCategory").data("kendoChart");
      if (assetChart) assetChart.destroy();
      this.recreateChartOnly(
        assetData,
        "#AssetCategory",
        "#AssetCategoryList",
        assetTitle,
        w,
        h,
      );

      const currChart: any = $("#CurrencyAnalysis").data("kendoChart");
      if (currChart) currChart.destroy();
      this.recreateChartOnly(
        currData,
        "#CurrencyAnalysis",
        "#CurrAnalysisList",
        currTitle,
        w,
        h,
      );
    } catch (e) {}
  }

  // Recreates the Kendo chart and list without touching the surrounding Bootstrap carousel
  // (so resizing doesn't bounce the user back to slide 0).
  private recreateChartOnly(
    charData: ChartModel[],
    chartSelector: string,
    listSelector: string,
    chartName: string,
    w: number,
    h: number,
  ) {
    const data =
      charData && charData.length > 0
        ? [...charData].sort((a, b) => Number(b.Value) - Number(a.Value))
        : [];
    const legendOnEmpty = data.length === 0 ? true : this.legendVisible();
    GenericChartsModule.KendoInitGenericDonutChart(
      chartSelector,
      data,
      "donut",
      "Value",
      "Description",
      chartName,
      legendOnEmpty,
      w,
      h,
      this.selectedCurrency || "",
    );
    GenericChartsModule.KendoGenericChartList(
      listSelector,
      data,
      this.createDonutChartList(),
    );
  }

  private createDonutChartList() {
    let donutColumnDefs = new Array<KendoColumnListDefinition>();
    donutColumnDefs.push(
      new KendoColumnListDefinition("Description", "#: Description #"),
    );
    donutColumnDefs.push(
      new KendoColumnListDefinition(
        "Value",
        '#: kendo.toString(Value, "n2") #',
        { style: "text-align: right;" },
      ),
    );
    donutColumnDefs.push(
      new KendoColumnListDefinition(
        "Percent",
        '#: kendo.toString(Percent*100, "0.00") + "%" #',
        { style: "text-align: right;" },
      ),
    );

    return donutColumnDefs;
  }

  ngAfterViewInit() {
    kendo.ui.progress(
      $(".donutChart-loading"),
      this._appStateProvider.fetchingAssetCategory,
    );
    kendo.ui.progress(
      $(".donutChart-loading"),
      this._appStateProvider.fetchingCurrAnalysis,
    );
    this.refreshDonutChart(
      this._insaService.chartDataCurrAnalysis,
      "#CurrencyAnalysis",
      "#CurrAnalysisList",
      (this._localize.getLocalizedResource("Res497") || "").toUpperCase(),
    );
    this.refreshDonutChart(
      this._insaService.chartDataAssetCategory,
      "#AssetCategory",
      "#AssetCategoryList",
      (this._localize.getLocalizedResource("Res3301") || "").toUpperCase(),
    );

    // Observe the home card column AND the inner carousel — at 1200-1399px the chart was rendering
    // before the column's final layout was settled, leaving an oversized SVG until the user resized
    // the window or navigated. Watching the outer card catches late layout changes (sidebar toggle,
    // route transitions) too. rAF debounce prevents resize-loop flooding.
    const card = document.getElementById("chartsCardCol");
    const carousel = document.getElementById("genericDonutCarousel");
    const target = card || carousel;
    if (target && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeRaf !== null) cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(() => {
          this.resizeRaf = null;
          if (carousel && carousel.offsetWidth > 0) {
            this.onResize(null);
          }
        });
      });
      this.resizeObserver.observe(target);
      if (card && carousel) this.resizeObserver.observe(carousel);
    }

    // Belt-and-braces: even with ResizeObserver, fire onResize at staggered ticks. Some
    // layout passes (CSS media query application, font load, ng *ngIf reveal) don't trigger
    // the observer if the element's box dimensions don't change but its rendering context
    // does. Re-running onResize is cheap because of the rAF guard inside.
    setTimeout(() => this.onResize(null), 0);
    setTimeout(() => this.onResize(null), 200);
    setTimeout(() => this.onResize(null), 600);
  }

  ngOnDestroy() {
    this.currAnalysisSubscription.unsubscribe();
    this.assetCategorySubscription.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeRaf !== null) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-charts/donut-chart.component.ts